/**
 * `kmiFetch` — typed envelope dispatcher for every Edge Function call.
 *
 * Implements the contract in `docs/frontend/035-frontend-app-shell-plan.md §7`
 * + `docs/10-runtime-architecture.md §5`. Every call:
 *   - attaches `Authorization: Bearer <access_token>` (when a session
 *     exists) and `apikey: <SUPABASE_ANON_KEY>`;
 *   - parses the typed `{ ok: true, data } | { ok: false, error }` envelope;
 *   - throws a `KmiApiError` carrying the wire `code` + safe extras for
 *     every non-2xx response.
 *
 * No retries. The plan §7 marks 500 + transient network errors as
 * retryable, but caching/retry land in a follow-up if the read load
 * actually justifies them; for the MVP shell, the explicit error state in
 * each page is sufficient signal.
 */

import { supabase } from '../auth/supabase';
import { env } from '../env';
import type { ErrorBody, ErrorEnvelope, SuccessEnvelope, WireErrorCode } from './types';

export class KmiApiError extends Error {
  readonly status: number;
  readonly code: WireErrorCode;
  readonly body: ErrorBody;

  constructor(status: number, body: ErrorBody) {
    super(`KMI API ${body.code} (HTTP ${status})`);
    this.name = 'KmiApiError';
    this.status = status;
    this.code = body.code;
    this.body = body;
  }
}

export class KmiNetworkError extends Error {
  readonly cause?: unknown;
  constructor(message: string, cause?: unknown) {
    super(message);
    this.name = 'KmiNetworkError';
    this.cause = cause;
  }
}

/**
 * Task 306A — what a binary caller says it is expecting back.
 *
 * `kmiFetchBinary` used to return `await res.blob()` for ANY 2xx response,
 * so a gateway interstitial, a proxy error page or any 200-with-JSON was
 * saved verbatim under a forced `.pdf` name: a file the browser downloads
 * and no reader can open, with no error surfacing anywhere. Declaring the
 * expectation turns that silent corruption into an honest failure.
 *
 * A REAL FILE CANNOT BE REJECTED BY THIS. `magic` is the same signature the
 * Edge Function itself requires before it will serve the bytes at all, so
 * anything that passes the server passes here.
 */
export interface BinaryExpectation {
  /** Compared against the response's media type, parameters stripped. */
  readonly contentType: string;
  /** ASCII signature the body must start with, e.g. `%PDF-`. */
  readonly magic: string;
}

export interface KmiFetchOptions {
  readonly method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  readonly query?: Readonly<Record<string, string | number | boolean | undefined>>;
  readonly body?: unknown;
  readonly signal?: AbortSignal;
  /** When true, sends only `apikey` (no `Authorization`). For anonymous endpoints. */
  readonly anonymous?: boolean;
  readonly headers?: Readonly<Record<string, string>>;
  /** `kmiFetchBinary` only. See {@link BinaryExpectation}. */
  readonly expectBinary?: BinaryExpectation;
}

export async function kmiFetch<T>(path: string, options: KmiFetchOptions = {}): Promise<T> {
  if (!env.functionsBaseUrl) {
    throw new KmiNetworkError(
      'API base URL is not configured. Set VITE_SUPABASE_URL or VITE_FUNCTIONS_BASE_URL.',
    );
  }

  const url = new URL(`${env.functionsBaseUrl.replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`);
  if (options.query) {
    for (const [key, value] of Object.entries(options.query)) {
      if (value === undefined || value === null) continue;
      const str = String(value);
      if (str.length === 0) continue;
      url.searchParams.set(key, str);
    }
  }

  const headers = new Headers();
  headers.set('Accept', 'application/json');
  if (env.supabaseAnonKey) headers.set('apikey', env.supabaseAnonKey);
  if (!options.anonymous) {
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    if (token) headers.set('Authorization', `Bearer ${token}`);
  }
  if (options.body !== undefined && options.body !== null) {
    headers.set('Content-Type', 'application/json');
  }
  if (options.headers) {
    for (const [k, v] of Object.entries(options.headers)) headers.set(k, v);
  }

  let res: Response;
  try {
    res = await fetch(url.toString(), {
      method: options.method ?? 'GET',
      headers,
      body:
        options.body !== undefined && options.body !== null ? JSON.stringify(options.body) : null,
      signal: options.signal,
      credentials: 'omit',
    });
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    throw new KmiNetworkError('Network request failed', cause);
  }

  let parsed: SuccessEnvelope<T> | ErrorEnvelope | null = null;
  try {
    parsed = (await res.json()) as SuccessEnvelope<T> | ErrorEnvelope;
  } catch {
    parsed = null;
  }

  if (parsed && parsed.ok === true) {
    return parsed.data;
  }
  const errorBody: ErrorBody =
    parsed && parsed.ok === false ? parsed.error : { code: mapStatusToWireCode(res.status) };
  const apiError = new KmiApiError(res.status, errorBody);

  // Central 401 handling per `docs/frontend/035-frontend-app-shell-plan.md §3.1 / §8.6`.
  // When an authenticated request is rejected as `unauthenticated` we clear the
  // supabase session so the MeContextProvider's auth listener flips to
  // `unauthenticated`, which in turn lets AuthGate redirect to
  // `/login?next=<path>` instead of stranding the user inside the shell with a
  // local error state. Anonymous calls (accept-invitation) never produce 401
  // by design — they collapse to `not_found` — so no sign-out fires there.
  // Concurrent 401 bursts (e.g. notifications poll + a page read fail at the
  // same time) dedupe through `pendingSignOut` so signOut() runs once per burst.
  if (!options.anonymous && apiError.code === 'unauthenticated') {
    if (!pendingSignOut) {
      pendingSignOut = supabase.auth
        .signOut()
        .then(() => undefined)
        .catch(() => undefined)
        .finally(() => {
          pendingSignOut = null;
        });
    }
  }

  throw apiError;
}

let pendingSignOut: Promise<void> | null = null;

/**
 * Task 303-A — the binary sibling of {@link kmiFetch}.
 *
 * One endpoint returns a FILE rather than a JSON envelope: the
 * agency⇄client contract PDF. It does so because the alternative — a
 * Supabase signed URL — puts the storage key, and therefore the bucket,
 * the organization id and the client id, into a URL the browser shows,
 * stores in history and hands to every proxy. Streaming the bytes is what
 * keeps that surface empty (303-A §2.10).
 *
 * This shares `kmiFetch`'s auth assembly and its central 401 sign-out so
 * the two transports cannot drift. On any non-2xx it parses the ordinary
 * error envelope and throws the same `KmiApiError`, so callers handle
 * failure exactly as they do everywhere else.
 */
export async function kmiFetchBinary(
  path: string,
  options: KmiFetchOptions = {},
): Promise<{ readonly blob: Blob; readonly filename: string | null }> {
  if (!env.functionsBaseUrl) {
    throw new KmiNetworkError(
      'API base URL is not configured. Set VITE_SUPABASE_URL or VITE_FUNCTIONS_BASE_URL.',
    );
  }

  const url = new URL(`${env.functionsBaseUrl.replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`);
  const headers = new Headers();
  headers.set('Accept', 'application/pdf, application/json');
  if (env.supabaseAnonKey) headers.set('apikey', env.supabaseAnonKey);
  if (!options.anonymous) {
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    if (token) headers.set('Authorization', `Bearer ${token}`);
  }
  if (options.body !== undefined && options.body !== null) {
    headers.set('Content-Type', 'application/json');
  }

  let res: Response;
  try {
    res = await fetch(url.toString(), {
      method: options.method ?? 'POST',
      headers,
      body:
        options.body !== undefined && options.body !== null ? JSON.stringify(options.body) : null,
      signal: options.signal,
      credentials: 'omit',
    });
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    throw new KmiNetworkError('Network request failed', cause);
  }

  if (!res.ok) {
    let parsed: ErrorEnvelope | null = null;
    try {
      parsed = (await res.json()) as ErrorEnvelope;
    } catch {
      parsed = null;
    }
    const errorBody: ErrorBody =
      parsed && parsed.ok === false ? parsed.error : { code: mapStatusToWireCode(res.status) };
    const apiError = new KmiApiError(res.status, errorBody);
    if (!options.anonymous && apiError.code === 'unauthenticated') {
      if (!pendingSignOut) {
        pendingSignOut = supabase.auth
          .signOut()
          .then(() => undefined)
          .catch(() => undefined)
          .finally(() => {
            pendingSignOut = null;
          });
      }
    }
    throw apiError;
  }

  const blob = await res.blob();
  if (options.expectBinary) {
    await assertExpectedBinary(blob, res.headers.get('content-type'), options.expectBinary);
  }

  return {
    blob,
    filename: parseContentDispositionFilename(res.headers.get('content-disposition')),
  };
}

/**
 * Prove the 2xx body is the file the caller asked for, not something that
 * merely arrived with a 200.
 *
 * Throws the ordinary `KmiApiError` so every caller's existing error
 * handling reports it — a new error class here would mean every page had to
 * learn a second failure shape to say the same thing. HTTP 502 is the
 * honest status: the request was authorised and answered, and what came
 * back was not the resource.
 *
 * Exported so the three refusals — wrong media type, empty body, wrong
 * signature — are executed by test rather than asserted by comment.
 */
export async function assertExpectedBinary(
  blob: Blob,
  contentTypeHeader: string | null,
  expected: BinaryExpectation,
): Promise<void> {
  const mediaType = (contentTypeHeader ?? '').split(';')[0].trim().toLowerCase();
  if (mediaType !== expected.contentType.toLowerCase()) {
    throw binaryError('unexpected_content_type');
  }
  if (blob.size === 0) {
    throw binaryError('empty_body');
  }

  const head = new Uint8Array(await blob.slice(0, expected.magic.length).arrayBuffer());
  if (head.length < expected.magic.length) {
    throw binaryError('truncated_body');
  }
  for (let i = 0; i < expected.magic.length; i += 1) {
    if (head[i] !== expected.magic.charCodeAt(i)) {
      throw binaryError('unexpected_signature');
    }
  }
}

function binaryError(detail: string): KmiApiError {
  return new KmiApiError(502, { code: 'internal_error', detail });
}

/**
 * Read the filename back out of `Content-Disposition`.
 *
 * `filename*` (RFC 5987) is preferred and is the one that carries Arabic;
 * the ASCII `filename=` is the fallback. Returns null rather than a guess
 * when neither is present, so the caller names the file itself instead of
 * inventing one from a header it did not get.
 */
export function parseContentDispositionFilename(header: string | null): string | null {
  if (!header) return null;
  const extended = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(header);
  if (extended?.[1]) {
    try {
      return decodeURIComponent(extended[1].trim());
    } catch {
      // fall through to the ASCII form
    }
  }
  const plain = /filename\s*=\s*"([^"]*)"/i.exec(header) ?? /filename\s*=\s*([^;]+)/i.exec(header);
  const value = plain?.[1]?.trim();
  return value && value.length > 0 ? value : null;
}

function mapStatusToWireCode(status: number): WireErrorCode {
  if (status === 401) return 'unauthenticated';
  if (status === 402) return 'entitlement_denied';
  if (status === 403) return 'permission_denied';
  if (status === 404) return 'not_found';
  if (status === 409) return 'conflict';
  if (status === 429) return 'rate_limited';
  if (status === 400) return 'validation_error';
  return 'internal_error';
}

export function isApiError(value: unknown): value is KmiApiError {
  return value instanceof KmiApiError;
}

export function isNetworkError(value: unknown): value is KmiNetworkError {
  return value instanceof KmiNetworkError;
}
